'use client'

import { useState, useEffect, useCallback, useRef, type RefObject } from 'react'
import { ChevronUp, ChevronDown } from 'lucide-react'
import { useTranslations } from 'next-intl'
import type { CoachMark } from './coach-mark-data'

interface CoachMarkTooltipProps {
  coachMark: CoachMark
  onDismiss: () => void
  onNext?: () => void
  hasNext: boolean
  containerRef: RefObject<HTMLDivElement | null>
}

interface Position {
  top: number
  left: number
  side: 'top' | 'bottom' | 'left' | 'right'
}

type VisState = 'visible' | 'scrolling' | 'offscreen-above' | 'offscreen-below' | 'obscured'

const ARROW_SIZE = 8
const TOOLTIP_MAX_WIDTH = 220
const TOOLTIP_PADDING = 8
const SCROLL_DEBOUNCE_MS = 250

function computePosition(
  targetRect: DOMRect,
  containerRect: DOMRect,
  preferredSide: CoachMark['side'],
  containerWidth: number,
): Position {
  const side = containerWidth < 400 ? 'bottom' : preferredSide
  const targetCenterX = targetRect.left - containerRect.left + targetRect.width / 2
  const targetCenterY = targetRect.top - containerRect.top + targetRect.height / 2
  const clampLeft = (rawLeft: number) =>
    Math.max(TOOLTIP_PADDING, Math.min(rawLeft, containerWidth - TOOLTIP_MAX_WIDTH - TOOLTIP_PADDING))

  switch (side) {
    case 'top':
      return { top: targetRect.top - containerRect.top - ARROW_SIZE - 4, left: clampLeft(targetCenterX - TOOLTIP_MAX_WIDTH / 2), side }
    case 'bottom':
      return { top: targetRect.bottom - containerRect.top + ARROW_SIZE + 4, left: clampLeft(targetCenterX - TOOLTIP_MAX_WIDTH / 2), side }
    case 'left':
      return { top: targetCenterY - 20, left: clampLeft(targetRect.left - containerRect.left - TOOLTIP_MAX_WIDTH - ARROW_SIZE - 4), side }
    case 'right':
      return { top: targetCenterY - 20, left: targetRect.right - containerRect.left + ARROW_SIZE + 4, side }
  }
}

function findTarget(container: HTMLElement, targetId: string): Element | null {
  return (
    container.querySelector(`[data-coach="${targetId}"]`) ??
    document.querySelector(`[data-coach="${targetId}"]`)
  )
}

/** Find the nearest scrollable ancestor within (or including) the container */
function findScrollParent(element: Element, container: HTMLElement): HTMLElement {
  let el: Element | null = element.parentElement
  while (el && el !== container) {
    const style = getComputedStyle(el)
    if (
      (style.overflowY === 'auto' || style.overflowY === 'scroll' ||
       style.overflow === 'auto' || style.overflow === 'scroll') &&
      el.scrollHeight > el.clientHeight
    ) {
      return el as HTMLElement
    }
    el = el.parentElement
  }
  // Check the container itself
  const cs = getComputedStyle(container)
  if (
    (cs.overflowY === 'auto' || cs.overflowY === 'scroll' ||
     cs.overflow === 'auto' || cs.overflow === 'scroll') &&
    container.scrollHeight > container.clientHeight
  ) {
    return container
  }
  return container
}

/** Determine if the target is above, below, obscured, or within the visible scroll area */
function getTargetVisibility(target: Element, container: HTMLElement): 'visible' | 'above' | 'below' | 'obscured' {
  const targetRect = target.getBoundingClientRect()
  const scrollParent = findScrollParent(target, container)
  const viewportRect = scrollParent.getBoundingClientRect()

  if (targetRect.bottom < viewportRect.top + 10) return 'above'
  if (targetRect.top > viewportRect.bottom - 10) return 'below'

  // Check if target is covered by an overlay (sheet, modal, etc.)
  const centerX = targetRect.left + targetRect.width / 2
  const centerY = targetRect.top + targetRect.height / 2
  const topElement = document.elementFromPoint(centerX, centerY)
  if (topElement && !target.contains(topElement) && !topElement.closest(`[data-coach="${target.getAttribute('data-coach')}"]`)) {
    return 'obscured'
  }

  return 'visible'
}

// ─── Scroll indicator shown when coach target is offscreen ──────────

function ScrollIndicator({
  direction,
  onClick,
  label,
}: {
  direction: 'up' | 'down'
  onClick: () => void
  label: string
}) {
  const Icon = direction === 'up' ? ChevronUp : ChevronDown
  return (
    <button
      onClick={onClick}
      className={`absolute left-1/2 -translate-x-1/2 z-[101] flex items-center gap-1 px-3 py-1.5 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-medium shadow-lg shadow-purple-500/30 animate-bounce ${
        direction === 'up' ? 'top-2' : 'bottom-2'
      }`}
    >
      <Icon className="h-3.5 w-3.5" />
      <span>{label}</span>
      <Icon className="h-3.5 w-3.5" />
    </button>
  )
}

// ─── Main tooltip ────────────────────────────────────────────────────

export function CoachMarkTooltip({ coachMark, onDismiss, onNext, hasNext, containerRef }: CoachMarkTooltipProps) {
  const t = useTranslations('demo')
  const [position, setPosition] = useState<Position | null>(null)
  const [exiting, setExiting] = useState(false)
  const [visState, setVisState] = useState<VisState>('visible')
  const scrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const mountedRef = useRef(true)

  const coachMarkRef = useRef(coachMark)
  coachMarkRef.current = coachMark

  const handleExit = useCallback((handler: () => void) => {
    if (exiting) return
    setExiting(true)
    setTimeout(handler, 250)
  }, [exiting])

  // Recalculate position from current DOM state
  const recalcPosition = useCallback(() => {
    const container = containerRef.current
    if (!container || !mountedRef.current) return

    const target = findTarget(container, coachMarkRef.current.target)
    if (!target) {
      onDismiss()
      return
    }

    const targetRect = target.getBoundingClientRect()
    const containerRect = container.getBoundingClientRect()
    setPosition(computePosition(targetRect, containerRect, coachMarkRef.current.side, containerRect.width))
  }, [containerRef, onDismiss])

  // Check visibility and update state
  const checkVisibility = useCallback(() => {
    const container = containerRef.current
    if (!container || !mountedRef.current) return

    const target = findTarget(container, coachMarkRef.current.target)
    if (!target) {
      onDismiss()
      return
    }

    const vis = getTargetVisibility(target, container)
    if (vis === 'visible') {
      recalcPosition()
      setVisState('visible')
    } else if (vis === 'obscured') {
      setVisState('obscured')
    } else {
      setVisState(vis === 'above' ? 'offscreen-above' : 'offscreen-below')
    }
  }, [containerRef, onDismiss, recalcPosition])

  // Scroll target into view (user-initiated via indicator click)
  const scrollToTarget = useCallback(() => {
    const container = containerRef.current
    if (!container) return

    const target = findTarget(container, coachMarkRef.current.target)
    if (!target) return

    target.scrollIntoView({ behavior: 'smooth', block: 'center' })
    // After scroll animation, recheck
    setTimeout(() => {
      if (!mountedRef.current) return
      checkVisibility()
    }, 450)
  }, [containerRef, checkVisibility])

  // Initial: check visibility and position or show indicator
  useEffect(() => {
    checkVisibility()
  // eslint-disable-next-line react-hooks/exhaustive-deps -- only on mount
  }, [])

  // Auto-dismiss when user clicks the target element (if dismissOnTargetClick is set)
  useEffect(() => {
    if (!coachMark.dismissOnTargetClick) return
    const container = containerRef.current
    if (!container) return

    const target = findTarget(container, coachMark.target)
    if (!target) return

    const handleClick = () => onDismiss()
    target.addEventListener('click', handleClick, { once: true })
    return () => target.removeEventListener('click', handleClick)
  // eslint-disable-next-line react-hooks/exhaustive-deps -- stable via coachMark identity
  }, [coachMark.id])

  // Scroll: hide instantly, debounce then check visibility
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const handleScroll = () => {
      if (!mountedRef.current) return
      setVisState('scrolling')

      if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current)
      scrollTimerRef.current = setTimeout(() => {
        if (!mountedRef.current) return
        checkVisibility()
      }, SCROLL_DEBOUNCE_MS)
    }

    container.addEventListener('scroll', handleScroll, { passive: true, capture: true })

    const resizeObserver = new ResizeObserver(() => {
      if (mountedRef.current) checkVisibility()
    })
    resizeObserver.observe(container)

    return () => {
      container.removeEventListener('scroll', handleScroll, { capture: true })
      resizeObserver.disconnect()
      if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- stable via refs
  }, [containerRef])

  // Periodically re-check visibility to detect dialogs opening/closing
  useEffect(() => {
    if (visState === 'scrolling') return // scroll has its own debounce
    const interval = setInterval(() => {
      if (mountedRef.current) checkVisibility()
    }, 500)
    return () => clearInterval(interval)
  }, [visState, checkVisibility])

  // Cleanup
  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])

  // ─── Centered overlay (no target element) ───────────────────────
  if (coachMark.target === '_center') {
    const handleAction = () => {
      if (coachMark.linkTo) {
        window.location.href = coachMark.linkTo
        return
      }
      setExiting(true)
      setTimeout(hasNext ? (onNext ?? onDismiss) : onDismiss, 250)
    }

    return (
      <div className={`absolute inset-0 z-[100] flex items-center justify-center p-6 bg-black/40 transition-all duration-300 ${
        exiting ? 'opacity-0' : 'animate-in fade-in duration-300'
      }`}>
        <div className="p-[1.5px] rounded-2xl bg-gradient-to-r from-purple-500 via-pink-500 to-emerald-500 shadow-2xl shadow-purple-500/30 max-w-sm w-full">
          <div className="bg-zinc-900 rounded-2xl px-5 py-4">
            <p className="text-sm text-white leading-relaxed">{t(coachMark.messageKey)}</p>
            <div className="flex items-center justify-end gap-3 mt-3 pt-2 border-t border-zinc-800">
              <button
                onClick={() => {
                  setExiting(true)
                  setTimeout(onDismiss, 250)
                }}
                className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
              >
                {t('dismiss')}
              </button>
              <button
                onClick={handleAction}
                className="text-xs font-semibold text-emerald-400 hover:text-emerald-300 transition-colors"
              >
                {coachMark.actionLabelKey ? t(coachMark.actionLabelKey) : t('ok')}
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // ─── Render offscreen indicator ─────────────────────────────────────

  if (visState === 'offscreen-above') {
    return <ScrollIndicator direction="up" onClick={scrollToTarget} label={t('viewTip')} />
  }
  if (visState === 'offscreen-below') {
    return <ScrollIndicator direction="down" onClick={scrollToTarget} label={t('viewTip')} />
  }

  // Hidden when target is covered by an overlay
  if (visState === 'obscured') return null

  if (!position) return null

  // ─── Tooltip positioning ────────────────────────────────────────────

  const style: React.CSSProperties = {
    position: 'absolute',
    zIndex: 100,
    maxWidth: TOOLTIP_MAX_WIDTH,
    left: position.left,
    ...(position.side === 'top'
      ? { top: position.top, transform: 'translateY(-100%)' }
      : { top: position.top }),
  }

  const arrowBase: React.CSSProperties = { position: 'absolute', width: 0, height: 0 }

  const arrowStyle: React.CSSProperties = (() => {
    switch (position.side) {
      case 'top':
        return { ...arrowBase, bottom: -ARROW_SIZE, left: '50%', transform: 'translateX(-50%)', borderLeft: `${ARROW_SIZE}px solid transparent`, borderRight: `${ARROW_SIZE}px solid transparent`, borderTop: `${ARROW_SIZE}px solid rgb(63 63 70)` }
      case 'bottom':
        return { ...arrowBase, top: -ARROW_SIZE, left: '50%', transform: 'translateX(-50%)', borderLeft: `${ARROW_SIZE}px solid transparent`, borderRight: `${ARROW_SIZE}px solid transparent`, borderBottom: `${ARROW_SIZE}px solid rgb(63 63 70)` }
      case 'left':
        return { ...arrowBase, right: -ARROW_SIZE, top: '50%', transform: 'translateY(-50%)', borderTop: `${ARROW_SIZE}px solid transparent`, borderBottom: `${ARROW_SIZE}px solid transparent`, borderLeft: `${ARROW_SIZE}px solid rgb(63 63 70)` }
      case 'right':
        return { ...arrowBase, left: -ARROW_SIZE, top: '50%', transform: 'translateY(-50%)', borderTop: `${ARROW_SIZE}px solid transparent`, borderBottom: `${ARROW_SIZE}px solid transparent`, borderRight: `${ARROW_SIZE}px solid rgb(63 63 70)` }
    }
  })()

  const isHidden = visState === 'scrolling'
  const visibilityClass = isHidden
    ? 'opacity-0 scale-95 pointer-events-none'
    : exiting
    ? 'opacity-0 scale-95 translate-y-1 pointer-events-none'
    : 'animate-in fade-in slide-in-from-bottom-2 duration-300'

  return (
    <div
      style={style}
      className={`pointer-events-auto transition-all duration-200 ${visibilityClass}`}
    >
      <div className="p-[1.5px] rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 shadow-xl shadow-purple-500/20">
        <div className="bg-zinc-900 rounded-xl px-3 py-2.5 relative">
          <p className="text-sm text-white leading-snug">{t(coachMark.messageKey)}</p>

          <div className="flex items-center justify-end gap-3 mt-2 pt-1.5 border-t border-zinc-800">
            {hasNext && (
              <button
                onClick={() => handleExit(onDismiss)}
                className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
              >
                {t('skip')}
              </button>
            )}
            <button
              onClick={() => handleExit(hasNext ? (onNext ?? onDismiss) : onDismiss)}
              className="text-xs font-medium text-purple-400 hover:text-purple-300 transition-colors"
            >
              {coachMark.actionLabelKey ? t(coachMark.actionLabelKey) : t('ok')}
            </button>
          </div>

          <div style={arrowStyle} />
        </div>
      </div>
    </div>
  )
}
